import { Injectable } from "@angular/core";

import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { dbToBoolean, dbToInt } from "@dffedb/util";
import { InstallationHttpClient, ParametreForAktuelleInstallation, UmbracoHttpClient } from "@e-forsyning/common/http";
import { LogService } from "@e-forsyning/common/log";

import { MitForbrugIndstillingerDto, TilesDto } from "./mit-forbrug-indstillinger.dto";
import { MitForbrugIndstillinger, Tile } from "./mit-forbrug-indstillinger.model";

@Injectable({ providedIn: "root" })
export class MitForbrugIndstillingerService {
    constructor(
        private readonly umbracoHttpClient: UmbracoHttpClient,
        private readonly installationHttpClient: InstallationHttpClient,
        private readonly log: LogService
    ) {}

    public hentIndstillinger(): Observable<MitForbrugIndstillinger> {
        return this.umbracoHttpClient.hentSideData<MitForbrugIndstillingerDto>("mit-forbrug").pipe(
            switchMap((dto) => this.hentTiles(dto.tilesOpsaetning, dto.tilescript).pipe(map((tiles) => ({ dto, tiles })))),
            map(({ dto, tiles }) => this.mapIndstillinger(dto, tiles))
        );
    }

    public hentTiles(tilesJson: string, fofScript: string): Observable<Tile[]> {
        const umbracoTiles = this.mapUmbracoTiles(tilesJson);
        return this.hentFofTiles(fofScript).pipe(
            map((fofTiles) => umbracoTiles.map((umbracoTile) => fofTiles.find((fofTile) => fofTile.type === umbracoTile.type) || umbracoTile))
        );
    }

    public mapUmbracoTiles(tilesJson: string): Tile[] {
        const tiles = (tilesJson && JSON.parse(tilesJson)) || {};
        return [1, 2, 3, 4, 5, 6]
            .map((id) =>
                this.mapTile(
                    tiles[`tile${id}`],
                    tiles[`tileOverskrift${id}`] || tiles[`tile${id}`],
                    tiles[`tileUnderOverskrift${id}`],
                    "",
                    "",
                    tiles[`externlink${id}`],
                    "",
                    ""
                )
            )
            .filter((t) => !!t.type);
    }

    public hentFofTiles(fofScript: string): Observable<Tile[]> {
        if (!fofScript) {
            return of([]);
        }
        return this.installationHttpClient.post<TilesDto>("api/gettileinfo", fofScript, ParametreForAktuelleInstallation.true).pipe(
            catchError((error) => {
                this.log.logError(error);
                return of({ oversigt: {} } as TilesDto);
            }),
            map((dto) => [
                this.mapTile(
                    "Budget",
                    "Budget",
                    dto.oversigt.forbrugText1,
                    dto.oversigt.forbrugText2,
                    dto.oversigt.forbrugIkon === "smiley-happy.png" ? "icon-glad" : "icon-sur",
                    null,
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "../../assets/images/tiles/Happy.jpg" : "../../assets/images/tiles/Sad.jpg",
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "Glad sparegris med drink" : "ked sparegris i regnvejr"
                ),
                this.mapTile(
                    "Temperatur",
                    "Temperatur",
                    dto.oversigt.temperaturText1,
                    dto.oversigt.temperaturText2,
                    dto.oversigt.temperaturIkon === "temp_warm.png" ? "icon-sol2" : "icon-frost",
                    null,
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "../../assets/images/tiles/Temperatur.jpg" : "../../assets/images/tiles/daarlig_afkoeling.jpg",
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "Termostat med hånd på" : "Mand ned jakke der fryser, i sofa"
                ),
                this.mapTile(
                    "Vejret",
                    "Vejret",
                    dto.oversigt.vejrText1,
                    dto.oversigt.vejrText2,
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "icon-varmt" : "icon-koldt",
                    null,
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "../../assets/images/tiles/Vejret.jpg" : "../../assets/images/tiles/Koldt.jpg",
                    dto.oversigt.vejrIkon === "temp_warm.png" ? "Skyer med blå himmel" : "Frost krystallerr"
                )
            ])
        );
    }

    public getTileinfo(fofScript: string): Observable<any> {
        return this.installationHttpClient.post("api/gettileinfo", fofScript, ParametreForAktuelleInstallation.true);
    }

    private mapTile(type: string, titel: string, tekst: string, tekst2: string, ikon: string, link: string, image: string, imageAlt: string): Tile {
        if (link && !link.toLowerCase().startsWith("http") && !link.startsWith("//")) {
            link = `//${link}`;
        }
        switch (type) {
            case "Mit Forbrug":
                return { type, titel, tekst, tekst2, link: "/mit-forbrug", ikon: "", img: { src: "../../assets/tiles/budget.jpg" } };
            case "Mine Dokumenter":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/mine-dokumenter",
                    ikon: "menu-minedokumenter",
                    img: { src: "../../assets/images/tiles/dokumenter.jpg", alt: "Dokument mapper" }
                };
            case "Sammenlign Forbrug":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/sammenlign-forbrug",
                    ikon: "menu-sammenlignforbrug",
                    img: { src: "../../assets/images/tiles/sammenlign_forbrug.jpg", alt: "Mønt stabler og et træhus" }
                };
            case "Mine Oplysninger":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/mine-oplysninger",
                    ikon: "menu-mineoplysninger",
                    img: { src: "../../assets/images/tiles/Mine_oplysninger.jpg", alt: "Mand med tablet" }
                };
            case "Beregnet Varmeregnskab":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/beregnet-varmeregnskab",
                    ikon: "menu-calculatedvarmeregnskab",
                    img: { src: "../../assets/images/tiles/beregnet_varmeregnskab.jpg", alt: "Lommeregner, pen og briller" }
                };
            case "Indtast Aflæsning":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/indtast-aflaesninger",
                    ikon: "menu-indtastaflaesninger",
                    img: { src: "../../assets/images/tiles/Indtast_aflæsninger.jpg", alt: "Papir med tal" }
                };
            case "Meld Flytning":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/meld-flytning",
                    ikon: "menu-meldflytning",
                    img: { src: "../../assets/images/tiles/Meld-flytning.jpg", alt: "Nøgler og model af hus" }
                };
            case "SMS Service":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/sms-service",
                    ikon: "menu-smsservice",
                    img: { src: "../../assets/images/tiles/SMS_service.jpg", alt: "Mobiltelefon" }
                };
            case "Kontakt":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/kontakt",
                    ikon: "menu-kontakt",
                    img: { src: "../../assets/images/tiles/kontakt.jpg", alt: "Terning med telefon,mail og @ ikon" }
                };
            case "Forbrugsoverblik":
                return {
                    type,
                    titel,
                    tekst,
                    tekst2,
                    link: "/forbrugsoverblik",
                    ikon: "menu-beskeder",
                    img: { src: "../../assets/images/tiles/Forbruger_overblik.jpg", alt: "Tablet med grafer på" }
                };
            default:
                return { type, titel, tekst, tekst2, link, ikon, img: { src: image, alt: imageAlt } };
        }
    }

    private mapIndstillinger(data: MitForbrugIndstillingerDto, tiles: Tile[]): MitForbrugIndstillinger {
        return {
            sideTitel: data.menuTekst,
            tiles,
            vejret: {
                titel: data.vejretOverskrift,
                tekst: data.vejretTekst
            },
            infoBoks: {
                infoboksOverskrift: data.infoboksOverskrift,
                infoboksBeskrivelse: data.infoboksBeskrivelse,
                infoboksFarveStatus: data.infoboksFarveStatus ? data.infoboksFarveStatus : null
            },
            forbrugsgraf: {
                startAar: dbToInt(data.startAarForEnergigrafVisning),
                defaultFane: this.mapValgtInterval(data.standardVisning),
                undertitel: data.undertitel,
                visKunEnergi: dbToBoolean(data.visKunEnergi),
                faner: {
                    aar: data.aar5Aar ? dbToBoolean(data.aar5Aar) : true,
                    maaned: data.maaned1Aar ? dbToBoolean(data.maaned1Aar) : true,
                    maanedSammenlign: data.maaned3Aar ? dbToBoolean(data.maaned3Aar) : true,
                    dag: data.dag1Maaned ? dbToBoolean(data.dag1Maaned) : true
                },
                taellevaerkKnap: {
                    tekst1: data.taellevaerksknap1Tekst,
                    tekst2: data.taellevaerksknap2Tekst
                },
                skjulForkerteDataFormular: dbToBoolean(data.skjulForkerteDataFormular)
            },
            temperaturgraf: {
                synlig: dbToBoolean(data.visTemperaturgrafen),
                startAar: dbToInt(data.startAarForTemperaturgrafVisning),
                defaultFane: this.mapValgtInterval(data.tempstandardVisning),
                tekst: {
                    titel: data.tempGrafBeskrivelseOverskrift,
                    tekst: data.tempGrafBeskrivelse
                },
                synligeData: {
                    fremloeb: dbToBoolean(data.visFremloebstemp),
                    retur: dbToBoolean(data.visReturTemp),
                    neutral: dbToBoolean(data.visNeutralTemp),
                    forventetRetur: dbToBoolean(data.visForventetTemp),
                    afkoeling: dbToBoolean(data.visAfkoeling)
                },
                faner: {
                    aar: data.tempGrafaar5Aar ? dbToBoolean(data.tempGrafaar5Aar) : true,
                    maaned: data.tempGrafMaaned1Aar ? dbToBoolean(data.tempGrafMaaned1Aar) : true,
                    dag: data.tempGrafDag1Maaned ? dbToBoolean(data.tempGrafDag1Maaned) : true
                }
            }
        };
    }

    private mapValgtInterval(value: string): number {
        switch (value) {
            case "Dag (1 måned)":
                return 0;
            case "Måned (1 år)":
                return 1;
            case "År (5 år)":
            default:
                return 3;
        }
    }
}
